import { WALK_SPEED, SPRINT_SPEED, clamp } from '@stillsun/shared';
import type { Vec3 } from '@stillsun/shared';
import { SOUND_MANIFEST } from './manifest.js';
import type { SoundDef, SoundId } from './manifest.js';

/**
 * STILLSUN client audio. Web Audio API only, no dependencies — see the README in this
 * folder for the exact integration points (which game event calls which method).
 *
 * The clips under `public/assets/gen/audio/` are peak-normalised (ASSETS.md's "Audio
 * notes"), so per-sound mixing lives in `manifest.ts`, not here. What lives here is the
 * mix graph, spatialisation, voice management and the small per-system helpers
 * (footsteps, weapon layering, Hymn vox/charge, ambience beds).
 */

// --------------------------------------------------------------------- mix graph

type BusName = 'sfx' | 'ui' | 'ambience';

/** Which bus each logical id mixes through. `sfx` = diegetic world sound (weapons,
 * footsteps, Hymn vox/charge). `ui` = 2D feedback tied to the HUD, even when it is
 * triggered by a world event (a hit marker, a loot pickup chime). `ambience` = beds. */
const BUS_FOR: Record<SoundId, BusName> = {
  carbine_shot: 'sfx',
  sidearm_shot: 'sfx',
  reload: 'sfx',
  footstep_regolith: 'sfx',
  footstep_metal: 'sfx',
  chorister_voice: 'sfx',
  cantor_charge: 'sfx',
  sexton_drone: 'ambience',
  beacon_hum: 'ambience',
  dimming_wind: 'ambience',
  dust_wind: 'ambience',
  downs_bed: 'ambience',
  lanternfall_bed: 'ambience',
  rain_bed: 'ambience',
  rain_hull: 'ambience',
  // The dropship. The two beds are the room you are riding in; the hits, the whip-bys, the klaxon
  // and the touchdown are world events with a position you can point at, so they mix as `sfx`.
  ship_engine: 'ambience',
  ship_wind: 'ambience',
  hull_hit: 'sfx',
  flak_pass: 'sfx',
  ship_alarm: 'sfx',
  touchdown: 'sfx',
  ui_tick: 'ui',
  ui_confirm: 'ui',
  hit_marker: 'ui',
  loot_pickup: 'ui',
};

interface Graph {
  master: GainNode;
  sfx: GainNode;
  ui: GainNode;
  ambience: GainNode;
}

// --------------------------------------------------------------------- panner tuning

const PANNER_MODEL: PanningModelType = 'equalpower'; // HRTF explicitly off, per spec
const DISTANCE_MODEL: DistanceModelType = 'inverse';
const REF_DISTANCE = 3;
const MAX_DISTANCE = 80;
const ROLLOFF_FACTOR = 1.2;

function makePanner(ctx: BaseAudioContext, pos: Vec3): PannerNode {
  const panner = ctx.createPanner();
  panner.panningModel = PANNER_MODEL;
  panner.distanceModel = DISTANCE_MODEL;
  panner.refDistance = REF_DISTANCE;
  panner.maxDistance = MAX_DISTANCE;
  panner.rolloffFactor = ROLLOFF_FACTOR;
  panner.positionX.value = pos.x;
  panner.positionY.value = pos.y;
  panner.positionZ.value = pos.z;
  return panner;
}

/** Per-frame reposition of an already-playing panner — smoothed to avoid zipper noise. */
function movePanner(ctx: BaseAudioContext, panner: PannerNode, pos: Vec3): void {
  const t = ctx.currentTime;
  const k = 0.05; // time constant; a moving Sexton/beacon glides, it doesn't snap
  panner.positionX.setTargetAtTime(pos.x, t, k);
  panner.positionY.setTargetAtTime(pos.y, t, k);
  panner.positionZ.setTargetAtTime(pos.z, t, k);
}

function dist(a: Vec3, b: Vec3): number {
  return Math.hypot(a.x - b.x, a.y - b.y, a.z - b.z);
}

function audioContextCtor(): typeof AudioContext {
  const w = window as unknown as {
    AudioContext?: typeof AudioContext;
    webkitAudioContext?: typeof AudioContext;
  };
  const ctor = w.AudioContext ?? w.webkitAudioContext;
  if (!ctor) throw new Error('Web Audio API unavailable in this browser');
  return ctor;
}

// --------------------------------------------------------------------- public shapes

export interface PlayOpts {
  /** Extra multiplier on top of the manifest's base gain (0..1 typical, not clamped). */
  gain?: number;
  /** Absolute playback rate multiplier, applied before random pitch jitter. */
  pitch?: number;
}

export interface Play3dOpts extends PlayOpts {
  /** Low-pass cutoff (Hz) to insert in the chain — see `weaponShot`'s >40 m case. */
  lowpass?: number;
}

export interface LoopHandle {
  /** Repositions a spatialised loop; a no-op on a loop started without a position. */
  setPosition(pos: Vec3): void;
  /** `gain` is a 0..1 multiplier on the manifest's base gain, not an absolute level. */
  setGain(gain: number, fadeMs?: number): void;
  /** Fades out over `fadeMs` (default: instant) then releases the source. */
  stop(fadeMs?: number): void;
}

const NULL_LOOP_HANDLE: LoopHandle = {
  setPosition: () => undefined,
  setGain: () => undefined,
  stop: () => undefined,
};

// --------------------------------------------------------------------- internals

interface ActiveVoice {
  source: AudioBufferSourceNode;
  gainNode: GainNode;
  panner: PannerNode | null;
  filter: BiquadFilterNode | null;
}

interface LoopVoice {
  source: AudioBufferSourceNode;
  gainNode: GainNode;
  panner: PannerNode | null;
}

interface ManagedLoop {
  handle: LoopHandle;
  lastUpdate: number;
}

interface ManagedBeacon {
  handle: LoopHandle;
  active: boolean;
}

const MAX_VOICES = 24;
const DIMMING_FADE_MS = 3000;
/** Zone-bed crossfade. Long: a gate crossing is a change of place, not a cut. */
const ZONE_BED_FADE_MS = 2500;
/** Every map bed `setZoneBed` knows about, so the crossfade can silence the others by name. */
const ZONE_BED_IDS: readonly SoundId[] = ['downs_bed', 'lanternfall_bed'];
/** A dust-storm gust: how long the swell runs, how loud it gets, and the calm after it. */
const GUST_SWELL_MS = 3500;
const GUST_PEAK_MULT = 1.5;
const GUST_GAP_MIN_MS = 4000;
const GUST_GAP_JITTER_MS = 5000;
const BEACON_FADE_MS = 1000;
/** How long a Sexton drone keeps humming after the last `sextonDrone()` heartbeat. */
const DRONE_TIMEOUT_MS = 1000;
const DRONE_STOP_FADE_MS = 300;
/** Distance past which a remote weapon shot gets the low-pass "far away" treatment. */
const DISTANT_SHOT_M = 40;
const DISTANT_SHOT_LOWPASS_HZ = 900;

/**
 * Weapon id (`WeaponDef.id`, 1..6) → its clip.
 *
 * Empty since Phase 2c: the two gun ids this table was keyed on went with the guns, and the
 * asset set has no melee or caster swing yet (`ASSETS.md` — nothing has been baked by
 * `tools/assetgen` for the six stat sticks). Every swing therefore falls through to the crack
 * below, which is wrong but audible; kept as a table rather than deleted so wiring the real
 * clips in is one line per weapon once they exist.
 */
const WEAPON_SOUND: Partial<Record<number, SoundId>> = {};

export class SoundManager {
  private ctx: AudioContext | null = null;
  /** Used only to decode clips before the first user gesture — see `load()`. */
  private offlineCtx: OfflineAudioContext | null = null;
  private graph: Graph | null = null;

  private readonly volumes = { master: 1, sfx: 1, ui: 1, ambience: 1 };
  private muted = false;

  private readonly buffers = new Map<SoundId, AudioBuffer[]>();
  private readonly loadedIds = new Set<SoundId>();
  private readonly failedIds = new Set<SoundId>();
  private readonly warned = new Set<SoundId>();

  private readonly voices: ActiveVoice[] = [];
  private readonly lastPlayedAt = new Map<SoundId, number>();
  private readonly lastVariant = new Map<SoundId, number>();

  private listenerPos: Vec3 | null = null;

  private readonly sextonDrones = new Map<number, ManagedLoop>();
  private readonly extractBeacons = new Map<number, ManagedBeacon>();
  private dimmingLoop: LoopHandle | null = null;
  /** The per-map ambience beds, kept alive at gain 0 between zones. See `setZoneBed`. */
  private readonly zoneBeds = new Map<SoundId, LoopHandle>();
  private zoneBedId: SoundId | null = null;
  private dimmingActive = false;

  // --- weather beds (render/weather.ts drives these off S2C.RaidState) ---------------------
  private dustLoop: LoopHandle | null = null;
  private rainLoop: LoopHandle | null = null;
  private rainHullLoop: LoopHandle | null = null;
  /** Smoothed 0..1 the beds are actually at, and the gust envelope riding on top of the dust. */
  private dustLevel = 0;
  private rainLevel = 0;
  /** Wall-clock ms the current gust swell runs until, and when the next one may start. */
  private gustUntil = 0;
  private nextGustAt = 0;
  /** 0 out in the open, 1 under the hull — the drips' whole gate. See `setNearHull`. */
  private nearHull = 0;

  /** Local-player footstep cadence. See the class below; construct more instances
   * yourself (e.g. one per remote player) if footsteps other than the local player's
   * are ever wanted — this default one is what `main.ts` should drive. */
  readonly footsteps = new FootstepPlayer(this);

  /** True once `unlock()` has created the real `AudioContext`. */
  get unlocked(): boolean {
    return this.ctx !== null;
  }

  /**
   * The live context and the ambience bus, for long-running generated sound (the hub's
   * requiem in `audio/requiem.ts`) that should ride the ambience volume and the mute like
   * everything else. `null` until `unlock()` has run inside a user gesture.
   */
  ambienceBus(): { ctx: AudioContext; bus: GainNode } | null {
    if (!this.ctx || !this.graph) return null;
    return { ctx: this.ctx, bus: this.graph.ambience };
  }

  // ------------------------------------------------------------------ lifecycle

  /**
   * Creates (or resumes) the real `AudioContext`. Must be called synchronously from a
   * user gesture handler — browsers refuse to start audio otherwise. See the README for
   * exactly where to call this (the Enter button).
   */
  unlock(): void {
    if (!this.ctx) {
      this.ctx = new (audioContextCtor())();
      this.graph = this.buildGraph(this.ctx);
    }
    if (this.ctx.state === 'suspended') void this.ctx.resume();
  }

  private buildGraph(ctx: AudioContext): Graph {
    const master = ctx.createGain();
    master.connect(ctx.destination);
    const sfx = ctx.createGain();
    sfx.connect(master);
    const ui = ctx.createGain();
    ui.connect(master);
    const ambience = ctx.createGain();
    ambience.connect(master);
    const graph: Graph = { master, sfx, ui, ambience };
    this.applyVolumesTo(graph);
    return graph;
  }

  private applyVolumesTo(graph: Graph): void {
    graph.master.gain.value = this.muted ? 0 : this.volumes.master;
    graph.sfx.gain.value = this.volumes.sfx;
    graph.ui.gain.value = this.volumes.ui;
    graph.ambience.gain.value = this.volumes.ambience;
  }

  private applyVolumes(): void {
    if (this.graph) this.applyVolumesTo(this.graph);
  }

  setMasterVolume(v: number): void {
    this.volumes.master = clamp(v, 0, 1);
    this.applyVolumes();
  }

  setSfxVolume(v: number): void {
    this.volumes.sfx = clamp(v, 0, 1);
    this.applyVolumes();
  }

  setUiVolume(v: number): void {
    this.volumes.ui = clamp(v, 0, 1);
    this.applyVolumes();
  }

  setAmbienceVolume(v: number): void {
    this.volumes.ambience = clamp(v, 0, 1);
    this.applyVolumes();
  }

  setMuted(muted: boolean): void {
    this.muted = muted;
    this.applyVolumes();
  }

  private busGainNode(id: SoundId): GainNode | null {
    if (!this.graph) return null;
    const bus = BUS_FOR[id];
    return bus === 'ui' ? this.graph.ui : bus === 'ambience' ? this.graph.ambience : this.graph.sfx;
  }

  // ---------------------------------------------------------------------- load

  /**
   * Decodes every clip in `manifest` once. Safe to call before `unlock()` — it decodes
   * against a throwaway `OfflineAudioContext` (decoding needs no user gesture) and the
   * resulting `AudioBuffer`s are reused once the real context exists. A missing or
   * malformed file fails soft: it is logged once via `console.warn` and every `play`
   * call for that id becomes a silent no-op forever after.
   */
  async load(manifest: Record<SoundId, SoundDef> = SOUND_MANIFEST): Promise<void> {
    const decodeCtx: BaseAudioContext = this.ctx ?? this.decodeContext();
    const ids = Object.keys(manifest) as SoundId[];
    await Promise.all(ids.map((id) => this.loadOne(id, manifest[id], decodeCtx)));
  }

  private decodeContext(): BaseAudioContext {
    this.offlineCtx ??= new OfflineAudioContext(1, 1, 44100);
    return this.offlineCtx;
  }

  private async loadOne(id: SoundId, def: SoundDef, ctx: BaseAudioContext): Promise<void> {
    const buffers: AudioBuffer[] = [];
    for (const url of def.urls) {
      try {
        const res = await fetch(url);
        if (!res.ok) throw new Error(`http ${String(res.status)}`);
        const bytes = await res.arrayBuffer();
        buffers.push(await ctx.decodeAudioData(bytes));
      } catch (err) {
        this.warnOnce(id, url, err);
      }
    }
    if (buffers.length > 0) {
      this.buffers.set(id, buffers);
      this.loadedIds.add(id);
    } else {
      this.failedIds.add(id);
    }
  }

  private warnOnce(id: SoundId, url: string, err: unknown): void {
    if (this.warned.has(id)) return;
    this.warned.add(id);
    console.warn(`[audio] "${id}" failed to load (${url})`, err);
  }

  // -------------------------------------------------------------- one-shot playback

  private nextVariant(id: SoundId, count: number): number {
    if (count <= 1) return 0;
    const last = this.lastVariant.get(id) ?? -1;
    let idx = Math.floor(Math.random() * count);
    if (idx === last) idx = (idx + 1) % count;
    this.lastVariant.set(id, idx);
    return idx;
  }

  private spawnVoice(
    id: SoundId,
    opts: PlayOpts | undefined,
    spatial: { pos: Vec3; lowpassHz?: number } | null,
  ): void {
    const ctx = this.ctx;
    if (!ctx || !this.graph) return;
    const buffers = this.buffers.get(id);
    if (!buffers || buffers.length === 0) return; // never loaded, or load() failed soft

    const def = SOUND_MANIFEST[id];
    const cooldown = def.cooldownMs ?? 0;
    const now = performance.now();
    if (cooldown > 0) {
      const last = this.lastPlayedAt.get(id) ?? -Infinity;
      if (now - last < cooldown) return;
    }
    this.lastPlayedAt.set(id, now);

    const buffer = buffers[this.nextVariant(id, buffers.length)];
    if (!buffer) return;

    const source = ctx.createBufferSource();
    source.buffer = buffer;
    const jitter = def.pitchJitter ?? 0;
    const jitterMult = jitter > 0 ? 1 + (Math.random() * 2 - 1) * jitter : 1;
    source.playbackRate.value = (opts?.pitch ?? 1) * jitterMult;

    const gainNode = ctx.createGain();
    gainNode.gain.value = def.gain * (opts?.gain ?? 1);

    let node: AudioNode = gainNode;
    source.connect(gainNode);

    let filter: BiquadFilterNode | null = null;
    if (spatial?.lowpassHz !== undefined) {
      filter = ctx.createBiquadFilter();
      filter.type = 'lowpass';
      filter.frequency.value = spatial.lowpassHz;
      node.connect(filter);
      node = filter;
    }

    let panner: PannerNode | null = null;
    if (spatial) {
      panner = makePanner(ctx, spatial.pos);
      node.connect(panner);
      node = panner;
    }

    const bus = this.busGainNode(id);
    if (!bus) return;
    node.connect(bus);

    const voice: ActiveVoice = { source, gainNode, panner, filter };
    this.voices.push(voice);
    this.stealOldestPastCap();

    source.onended = () => {
      const idx = this.voices.indexOf(voice);
      if (idx >= 0) this.voices.splice(idx, 1);
      source.disconnect();
      gainNode.disconnect();
      panner?.disconnect();
      filter?.disconnect();
    };
    source.start();
  }

  private stealOldestPastCap(): void {
    while (this.voices.length > MAX_VOICES) {
      const oldest = this.voices.shift();
      if (!oldest) break;
      try {
        oldest.source.stop();
      } catch {
        /* already stopped/ended */
      }
    }
  }

  /** Plain 2D playback (UI sounds, local weapon fire, non-positional feedback). */
  play(id: SoundId, opts?: PlayOpts): void {
    this.spawnVoice(id, opts, null);
  }

  /** Spatialised playback via a `PannerNode` — see the module header for the tuning. */
  play3d(id: SoundId, pos: Vec3, opts?: Play3dOpts): void {
    const spatial = opts?.lowpass !== undefined ? { pos, lowpassHz: opts.lowpass } : { pos };
    this.spawnVoice(id, opts, spatial);
  }

  // ------------------------------------------------------------------------ listener

  /** Call once per frame with the local camera's world position/forward/up. */
  setListener(pos: Vec3, forward: Vec3, up: Vec3): void {
    this.listenerPos = { x: pos.x, y: pos.y, z: pos.z };
    const ctx = this.ctx;
    if (!ctx) return;
    const l = ctx.listener;
    const t = ctx.currentTime;
    const k = 0.05;
    l.positionX.setTargetAtTime(pos.x, t, k);
    l.positionY.setTargetAtTime(pos.y, t, k);
    l.positionZ.setTargetAtTime(pos.z, t, k);
    l.forwardX.setTargetAtTime(forward.x, t, k);
    l.forwardY.setTargetAtTime(forward.y, t, k);
    l.forwardZ.setTargetAtTime(forward.z, t, k);
    l.upX.setTargetAtTime(up.x, t, k);
    l.upY.setTargetAtTime(up.y, t, k);
    l.upZ.setTargetAtTime(up.z, t, k);
  }

  // ---------------------------------------------------------------------------- loops

  private buildLoopVoice(id: SoundId, pos: Vec3 | null): LoopVoice | null {
    const ctx = this.ctx;
    if (!ctx || !this.graph) return null;
    const buffers = this.buffers.get(id);
    if (!buffers || buffers.length === 0) return null;
    const buffer = buffers[0];
    if (!buffer) return null;

    const source = ctx.createBufferSource();
    source.buffer = buffer;
    source.loop = true;

    const gainNode = ctx.createGain();
    gainNode.gain.value = SOUND_MANIFEST[id].gain;

    let node: AudioNode = gainNode;
    source.connect(gainNode);

    let panner: PannerNode | null = null;
    if (pos) {
      panner = makePanner(ctx, pos);
      node.connect(panner);
      node = panner;
    }

    const bus = this.busGainNode(id);
    if (!bus) return null;
    node.connect(bus);

    return { source, gainNode, panner };
  }

  private toHandle(id: SoundId, voice: LoopVoice): LoopHandle {
    let stopped = false;
    return {
      setPosition: (pos: Vec3) => {
        const ctx = this.ctx;
        if (ctx && voice.panner) movePanner(ctx, voice.panner, pos);
      },
      setGain: (gain: number, fadeMs = 0) => {
        const ctx = this.ctx;
        if (!ctx) return;
        const target = SOUND_MANIFEST[id].gain * gain;
        const now = ctx.currentTime;
        if (fadeMs > 0) {
          voice.gainNode.gain.cancelScheduledValues(now);
          voice.gainNode.gain.setValueAtTime(voice.gainNode.gain.value, now);
          voice.gainNode.gain.linearRampToValueAtTime(target, now + fadeMs / 1000);
        } else {
          voice.gainNode.gain.cancelScheduledValues(now);
          voice.gainNode.gain.value = target;
        }
      },
      stop: (fadeMs = 0) => {
        if (stopped) return;
        stopped = true;
        const ctx = this.ctx;
        if (!ctx) return;
        const now = ctx.currentTime;
        if (fadeMs > 0) {
          voice.gainNode.gain.cancelScheduledValues(now);
          voice.gainNode.gain.setValueAtTime(voice.gainNode.gain.value, now);
          voice.gainNode.gain.linearRampToValueAtTime(0, now + fadeMs / 1000);
        }
        const stopAt = now + (fadeMs > 0 ? fadeMs / 1000 + 0.02 : 0);
        try {
          voice.source.stop(stopAt);
        } catch {
          /* already stopped */
        }
        voice.source.onended = () => {
          voice.source.disconnect();
          voice.gainNode.disconnect();
          voice.panner?.disconnect();
        };
      },
    };
  }

  /** Starts a managed loop; `pos` omitted plays it 2D (dimming wind). Always returns a
   * usable handle, even before `unlock()` or if the clip failed to load — every method
   * on it is then simply a no-op. */
  startLoop(id: SoundId, pos?: Vec3): LoopHandle {
    const voice = this.buildLoopVoice(id, pos ?? null);
    if (!voice) return NULL_LOOP_HANDLE;
    voice.source.start();
    return this.toHandle(id, voice);
  }

  // ------------------------------------------------------------------ per-frame tick

  /**
   * Call once per frame (any dt works; only wall-clock heartbeats are timed). Reaps
   * Sexton drones nobody has refreshed in the last second — see `sextonDrone()`.
   */
  update(dt: number): void {
    void dt;
    const now = performance.now();
    for (const [id, entry] of this.sextonDrones) {
      if (now - entry.lastUpdate > DRONE_TIMEOUT_MS) {
        entry.handle.stop(DRONE_STOP_FADE_MS);
        this.sextonDrones.delete(id);
      }
    }
  }

  /** Stops every managed loop (Sexton drones, beacons, the Dimming wind) and every
   * live one-shot voice, without discarding decoded buffers or the `AudioContext`.
   * Call this leaving a raid (e.g. `returnToHub()`) so nothing keeps humming into the
   * hub. */
  reset(): void {
    for (const entry of this.sextonDrones.values()) entry.handle.stop(0);
    this.sextonDrones.clear();
    for (const entry of this.extractBeacons.values()) entry.handle.stop(0);
    this.extractBeacons.clear();
    if (this.dimmingLoop) {
      this.dimmingLoop.stop(0);
      this.dimmingLoop = null;
      this.dimmingActive = false;
    }
    for (const loop of [this.dustLoop, this.rainLoop, this.rainHullLoop]) loop?.stop(0);
    this.dustLoop = null;
    this.rainLoop = null;
    this.rainHullLoop = null;
    this.dustLevel = 0;
    this.rainLevel = 0;
    this.nearHull = 0;
    this.gustUntil = 0;
    this.nextGustAt = 0;
    for (const voice of [...this.voices]) {
      try {
        voice.source.stop();
      } catch {
        /* already stopped */
      }
    }
  }

  // ------------------------------------------------------------------------ weapons

  /**
   * `isLocal` shots are always 2D at full manifest gain — the player's own gun is never
   * spatialised, or it would pan with the muzzle offset and read as wrong. Remote shots
   * are 3D, and past `DISTANT_SHOT_M` (40 m) get a low-pass layer so a distant firefight
   * reads as distant rather than merely quiet.
   */
  weaponShot(weaponId: number, pos: Vec3, isLocal: boolean): void {
    const id: SoundId = WEAPON_SOUND[weaponId] ?? 'carbine_shot';
    if (isLocal) {
      this.play(id);
      return;
    }
    const far = this.listenerPos !== null && dist(this.listenerPos, pos) > DISTANT_SHOT_M;
    const opts: Play3dOpts = far ? { lowpass: DISTANT_SHOT_LOWPASS_HZ } : {};
    this.play3d(id, pos, opts);
  }

  // --------------------------------------------------------------------------- Hymns

  /** Chorister alert bark. `kind` is a hook for future Hymn vox; only 'chorister' has a
   * clip today (`chorister_voice`, 2 variants, round-robin — see manifest.ts). */
  hymnVoice(kind: 'chorister', pos: Vec3): void {
    void kind;
    this.play3d('chorister_voice', pos);
  }

  /**
   * Cantor rail-lance charge tell. Plays `cantor_charge` once, ramping its playback rate
   * 0.8x -> 1.4x across `durationMs` so the rising whine's peak lands with the charge's
   * own visual peak (see `entities.ts`'s `EntityState.Charging` ramp) regardless of how
   * long the server's charge actually runs, then releases itself — nothing to stop by
   * hand, even if the Cantor dies mid-charge (the source simply plays out or is cut by
   * its own scheduled fade).
   */
  cantorCharge(pos: Vec3, durationMs: number): void {
    const ctx = this.ctx;
    const voice = this.buildLoopVoice('cantor_charge', pos);
    if (!ctx || !voice) return;
    voice.source.loop = false;

    const now = ctx.currentTime;
    const dur = Math.max(0.05, durationMs / 1000);
    voice.source.playbackRate.setValueAtTime(0.8, now);
    voice.source.playbackRate.linearRampToValueAtTime(1.4, now + dur);

    const fadeStart = Math.max(now, now + dur - 0.15);
    voice.gainNode.gain.setValueAtTime(voice.gainNode.gain.value, fadeStart);
    voice.gainNode.gain.linearRampToValueAtTime(0, now + dur + 0.05);

    voice.source.onended = () => {
      voice.source.disconnect();
      voice.gainNode.disconnect();
      voice.panner?.disconnect();
    };
    voice.source.start(now);
    try {
      voice.source.stop(now + dur + 0.12);
    } catch {
      /* already stopped */
    }
  }

  /**
   * Managed Sexton organ drone, keyed by entity id. Call every frame a Sexton is live
   * (e.g. once per `EntityManager` tick for each non-dead Sexton) — the loop starts on
   * first contact, follows `pos` after that, and auto-stops (see `update()`) if this
   * isn't called again within a second, which is what happens when the Sexton dies or
   * leaves the snapshot.
   */
  sextonDrone(entityId: number, pos: Vec3): void {
    const now = performance.now();
    const existing = this.sextonDrones.get(entityId);
    if (existing) {
      existing.handle.setPosition(pos);
      existing.lastUpdate = now;
      return;
    }
    this.sextonDrones.set(entityId, {
      handle: this.startLoop('sexton_drone', pos),
      lastUpdate: now,
    });
  }

  // ------------------------------------------------------------------------ ambience

  /**
   * The zone bed: one long ambience loop per map, crossfaded when the player crosses a gate.
   *
   * Kept alive at gain 0 rather than stopped, for exactly the reason `setDimming` keeps its one:
   * a bed restarted from a cold buffer clicks, and Lanternfall <-> the Vigil Downs is a seamless
   * hand-off a player can make twice a minute. Both loops exist from the first call and only
   * their gains move, so the steady-state cost of a gate crossing is two ramps on a `GainNode`.
   *
   * `null` (a map with no bed of its own - the yard, the Spine, which has its own weather beds)
   * fades both out and leaves them running silently.
   */
  setZoneBed(mapId: string | null): void {
    const want: SoundId | null =
      mapId === 'vigil-downs' ? 'downs_bed' : mapId === 'lanternfall' ? 'lanternfall_bed' : null;
    if (want === this.zoneBedId) return;
    this.zoneBedId = want;
    for (const id of ZONE_BED_IDS) {
      let loop = this.zoneBeds.get(id);
      if (!loop) {
        // Nothing is allocated for a map that never plays a bed: the first crossing into a zone
        // builds both, so the *second* crossing is a pure gain ramp with nothing to decode.
        if (want === null) continue;
        loop = this.startLoop(id);
        loop.setGain(0);
        this.zoneBeds.set(id, loop);
      }
      loop.setGain(id === want ? 1 : 0, ZONE_BED_FADE_MS);
    }
  }

  /** Fades `dimming_wind` in/out over 3 s. The loop itself is created once and kept
   * alive at gain 0 between activations, so re-entering the Dimming never restarts the
   * clip from a cold start (which would click). */
  setDimming(active: boolean): void {
    if (!this.dimmingLoop) {
      this.dimmingLoop = this.startLoop('dimming_wind');
      this.dimmingLoop.setGain(0);
    }
    if (active === this.dimmingActive) return;
    this.dimmingActive = active;
    this.dimmingLoop.setGain(active ? 1 : 0, DIMMING_FADE_MS);
  }

  /**
   * The room's weather bed. Called every frame from the render loop with `render/weather.ts`'s
   * *smoothed* dust and rain values, so this never has to do its own easing for the ramp — what
   * it adds on top is the gusting, which is a sound-only idea and has no picture to match.
   *
   * Three loops, created once on the first call and then kept alive at gain 0 forever, for the
   * same reason `setDimming` keeps its one: restarting a 20 s bed from a cold buffer clicks, and
   * a storm that arrives and leaves twice in a raid would click four times. The steady-state
   * cost of this method is three gain writes on a `GainNode`, which is why it is safe to call at
   * 144 Hz.
   */
  setWeather(dust: number, rain: number): void {
    const d = dust < 0 ? 0 : dust > 1 ? 1 : dust;
    const r = rain < 0 ? 0 : rain > 1 ? 1 : rain;
    // Nothing to do, and nothing to build, until there is actually weather. A raid that never
    // sees a storm never allocates these three sources.
    if (d <= 0 && r <= 0 && this.dustLoop === null && this.rainLoop === null) {
      this.dustLevel = 0;
      this.rainLevel = 0;
      return;
    }
    if (!this.dustLoop) {
      this.dustLoop = this.startLoop('dust_wind');
      this.dustLoop.setGain(0);
      this.rainLoop = this.startLoop('rain_bed');
      this.rainLoop.setGain(0);
      this.rainHullLoop = this.startLoop('rain_hull');
      this.rainHullLoop.setGain(0);
    }
    this.dustLevel = d;
    this.rainLevel = r;
    this.writeWeatherGains();
  }

  /**
   * How enclosed the listener is, 0 (open dunes) .. 1 (inside the wreck). Only the rain's drips
   * read it: rain on open sand is the bed, rain on the hull is a different sound and it belongs
   * to the plating overhead, not to the sky. `main.ts` already knows which surface the player is
   * standing on for footsteps, and passes the same flag here.
   */
  setNearHull(t: number): void {
    const k = t < 0 ? 0 : t > 1 ? 1 : t;
    if (Math.abs(k - this.nearHull) < 0.01) return;
    this.nearHull = k;
    this.writeWeatherGains();
  }

  /**
   * Gusts. A storm's wind is not a constant — it leans on you and lets go — and a flat bed reads
   * as a broken loop no matter how long the clip is. Each gust is a 3.5 s swell scheduled on the
   * `GainNode` itself (two ramps, no per-frame writes), lifting the bed to 1.5x and settling
   * back, with 4-9 s of calm between them. Rain gets none of this: rain is steady, and a gusting
   * rain would read as the mix breathing.
   */
  private writeWeatherGains(): void {
    const now = performance.now();
    // The gust is a state, not a callback. A `setTimeout` to schedule the settle would either
    // have to suppress the steady write for its whole 1.7 s — dropping any storm ramp that
    // happened during it — or fight with it, and it would still be in flight after `reset()`.
    // Reading the clock costs nothing and this method already runs every frame.
    const gusting = now < this.gustUntil;
    if (!gusting && this.dustLevel > 0.15 && now >= this.nextGustAt) {
      this.gustUntil = now + GUST_SWELL_MS;
      this.nextGustAt = this.gustUntil + GUST_GAP_MIN_MS + Math.random() * GUST_GAP_JITTER_MS;
      this.dustLoop?.setGain(this.dustLevel * GUST_PEAK_MULT, GUST_SWELL_MS * 0.9);
    } else {
      // Steady, or settling out of a swell. Either way the *current* dust level is written, so
      // a storm that ramps mid-gust is never a second and a half behind the picture.
      const target = gusting ? this.dustLevel * GUST_PEAK_MULT : this.dustLevel;
      this.dustLoop?.setGain(target, gusting ? 300 : 1200);
    }
    this.rainLoop?.setGain(this.rainLevel, 400);
    // The drips are the product of the two: it has to be raining *and* you have to be under
    // something for water to be landing on plating above your head.
    this.rainHullLoop?.setGain(this.rainLevel * this.nearHull, 500);
  }

  /** Managed extract-beacon hum, keyed by extract id. Safe to call every frame (or every
   * `raidState`/`beacons.update`) — position always refreshes, and the fade only runs
   * when `active` actually flips, so steady-state calls are nearly free. */
  beacon(id: number, pos: Vec3, active: boolean): void {
    let entry = this.extractBeacons.get(id);
    if (!entry) {
      const handle = this.startLoop('beacon_hum', pos);
      handle.setGain(0);
      entry = { handle, active: false };
      this.extractBeacons.set(id, entry);
    } else {
      entry.handle.setPosition(pos);
    }
    if (active !== entry.active) {
      entry.active = active;
      entry.handle.setGain(active ? 1 : 0, BEACON_FADE_MS);
    }
  }
}

// ------------------------------------------------------------------------ footsteps

const STRIDE_WALK_M = 0.55;
const STRIDE_SPRINT_M = 0.72;
const CROUCH_GAIN_MULT = 0.6;
/** Below this speed the player reads as stopped, whatever tiny drift the predictor
 * leaves in `hspeed` at rest. */
const MOVE_EPSILON_MPS = 0.15;

/**
 * Stride-based footstep cadence for one moving thing (the local player by default —
 * `SoundManager.footsteps`). Construct additional instances if remote-player footsteps
 * are ever wanted; each instance tracks its own stride phase independently.
 */
export class FootstepPlayer {
  private readonly sound: SoundManager;
  private distanceM = 0;

  constructor(sound: SoundManager) {
    this.sound = sound;
  }

  /**
   * `crouched` and `pos` are both optional beyond the base (dt, hspeed, grounded,
   * surface, sprinting) signature: `crouched` carries the 0.6x gain multiplier the task
   * calls for (there is no way to derive stance from speed alone), and `pos` lets the
   * same helper spatialise footsteps for something other than the local listener later.
   * Omit both for local-player footsteps.
   */
  update(
    dt: number,
    hspeed: number,
    grounded: boolean,
    surface: 'regolith' | 'metal',
    sprinting: boolean,
    crouched = false,
    pos?: Vec3,
  ): void {
    if (!grounded || hspeed < MOVE_EPSILON_MPS) {
      // Landing or stopping resets the phase — the first step after moving again is
      // never a stale fraction of a stride late.
      this.distanceM = 0;
      return;
    }
    this.distanceM += hspeed * dt;
    const stride = sprinting ? STRIDE_SPRINT_M : STRIDE_WALK_M;
    while (this.distanceM >= stride) {
      this.distanceM -= stride;
      this.step(hspeed, surface, sprinting, crouched, pos);
    }
  }

  /**
   * One footfall, right now. The local player uses this instead of `update()`: the weapon
   * view model reports each bottom-of-bob plant (`takeFootfalls()`), so the sound lands on
   * the visual step rather than on a stride clock that drifts away from it (Chris,
   * 2026-09-04: "the sound effects for running don't line up with footsteps").
   */
  step(
    hspeed: number,
    surface: 'regolith' | 'metal',
    sprinting: boolean,
    crouched = false,
    pos?: Vec3,
  ): void {
    const id: SoundId = surface === 'metal' ? 'footstep_metal' : 'footstep_regolith';
    const speedGain = clamp(hspeed / (sprinting ? SPRINT_SPEED : WALK_SPEED), 0, 1);
    const gain = speedGain * (crouched ? CROUCH_GAIN_MULT : 1);
    if (pos) this.sound.play3d(id, pos, { gain });
    else this.sound.play(id, { gain });
  }
}

/** Ready-to-use singleton, matching the `settings`/`session` pattern elsewhere in
 * `apps/client/src` — import this rather than constructing your own `SoundManager`. */
export const sound = new SoundManager();
